/**
 * Close-button hover micro-interaction.
 *
 * Drawer- en modal-close-buttons gebruiken het drie-bars X-patroon
 * (zelfde als de hamburger toggle in `_menu-toggle.scss`). Bij hover
 * draait de hele button 90° met een subtiele scale-bounce — Motion.js
 * animeert `rotate` + `scale` op het button-element zelf zodat de
 * geneste `<span>`-bars als geheel meedraaien.
 *
 * Andere hover-feedback in het thema (CTA-lift, select-trigger
 * border-darkening, chevron-bounce) is bewust CSS-only: korter,
 * goedkoper en zonder JS-bundle-cost. Deze module is alleen voor de
 * close-button waar we een speelser, expliciet effect willen.
 *
 * Reduced-motion: init no-op'ed; geen mouseenter listener gebonden.
 *
 * @module modules/ui/close-button-hover
 */

import { animate } from 'motion';

import { prefersReducedMotion } from './motion-utils.js';

const CLOSE_SELECTORS = [
    '.archive-drawer__close',
    '.archive-modal__close',
];

const COMBINED_SELECTOR = CLOSE_SELECTORS.join(',');

let bound = false;

/**
 * Public init. Bindt één document-level `mouseenter`-listener (capture
 * fase zodat we ook spans/icons binnen de button opvangen) die naar
 * de gemeenschappelijke close-button selectors zoekt.
 *
 * Idempotent via een module-level flag — herhaalde calls binden niet
 * opnieuw.
 *
 * @returns {void}
 */
export function initCloseButtonHover() {
    if (prefersReducedMotion()) return;
    if (bound) return;
    bound = true;

    document.addEventListener('mouseenter', handleMouseEnter, true);
}

/**
 * Vindt de close-button vanaf het event-target naar boven, of null
 * wanneer het event buiten een close-button valt.
 *
 * @param {EventTarget|null} target
 *
 * @returns {HTMLElement|null}
 */
function findCloseButton(target) {
    if (!(target instanceof Element)) return null;
    const match = target.closest(COMBINED_SELECTOR);
    return match instanceof HTMLElement ? match : null;
}

/**
 * Trigger de spin-and-bounce animatie. Een `dataset.animating`-flag
 * voorkomt dat snel-opeenvolgende mouseenters de animatie herstarten
 * en visuele glitches veroorzaken; na voltooiing wordt de flag gewist
 * en de inline-style gereset zodat de volgende hover weer schoon
 * vanuit ruststand begint.
 *
 * @param {MouseEvent} event
 *
 * @returns {void}
 */
function handleMouseEnter(event) {
    const btn = findCloseButton(event.target);
    if (!btn) return;
    if (btn.dataset.animating === 'true') return;

    btn.dataset.animating = 'true';

    const playback = animate(
        btn,
        {
            rotate: [0, 90],
            scale: [1, 1.15, 1],
        },
        {
            duration: 0.4,
            ease: [0.34, 1.56, 0.64, 1],
        },
    );

    const cleanup = () => {
        delete btn.dataset.animating;
        // Inline-styles wissen zodat eventuele CSS-transforms (bv. de
        // X-bars op kinderen) hun normale ruststand herpakken zonder
        // overruled te worden door achtergebleven Motion.js-output.
        btn.style.removeProperty('rotate');
        btn.style.removeProperty('scale');
        btn.style.removeProperty('transform');
    };

    if (playback && typeof (/** @type {{then?: unknown}} */ (playback).then) === 'function') {
        /** @type {Promise<unknown>} */ (playback).then(cleanup, cleanup);
    } else {
        // Defensieve fallback: als animate() geen Promise teruggeeft
        // (oudere Motion.js-versies of mock in tests), reset na de
        // verwachte duration via setTimeout zodat de flag niet blijft
        // hangen.
        setTimeout(cleanup, 400);
    }
}
